import type { DateFormat } from 'types/date';

/**
 * Provides a Map of date formats to their translation configurations.
 *
 * @param {Object} params - Function parameters.
 * @param {Function} params.message - Function to fetch translated messages.
 * @returns {Map<DateFormat, Object>} Map of date formats to translation configs.
 */
export const getDateFormatTranslationConfig = ({
  message,
}: {
  message: (params: { id: DateFormat; comment: string }) => string;
}): Map<DateFormat, { translation: string }> => new Map([
  ['YYYY-MM-DD', { translation: message({ id: 'YYYY-MM-DD', comment: 'Date format' }) }],
  ['MM-DD-YYYY', { translation: message({ id: 'MM-DD-YYYY', comment: 'Date format' }) }],
  ['DD-MM-YYYY', { translation: message({ id: 'DD-MM-YYYY', comment: 'Date format' }) }],
  ['YYYY/MM/DD', { translation: message({ id: 'YYYY/MM/DD', comment: 'Date format' }) }],
  ['MM/DD/YYYY', { translation: message({ id: 'MM/DD/YYYY', comment: 'Date format' }) }],
  ['DD/MM/YYYY', { translation: message({ id: 'DD/MM/YYYY', comment: 'Date format' }) }],
  ['YYYY.MM.DD', { translation: message({ id: 'YYYY.MM.DD', comment: 'Date format' }) }],
  ['MM.DD.YYYY', { translation: message({ id: 'MM.DD.YYYY', comment: 'Date format' }) }],
  ['DD.MM.YYYY', { translation: message({ id: 'DD.MM.YYYY', comment: 'Date format' }) }],
]);

/**
 * Gets the translation for a date format.
 *
 * @param {Object} params - Function parameters.
 * @param {Function} params.message - Fetches translated messages.
 * @param {DateFormat} params.dateFormat - Date format to translate.
 * @returns {string} Translated message or an empty string if not found.
 */
export const getTranslationForDateFormat = ({
  message,
  dateFormat,
}: {
  message: (params: { id: DateFormat; comment: string }) => string;
  dateFormat: DateFormat;
}): string => {
  const dataFormatTranslationConfig = getDateFormatTranslationConfig({ message }).get(dateFormat);
  if (!dataFormatTranslationConfig) {
    return '';
  }
  return dataFormatTranslationConfig.translation;
};
